/**
 * Lightweight analytics dispatcher.
 *
 * Sends events directly to the Google tag (GA4) when available, while keeping
 * the existing dataLayer/Plausible/PostHog/DOM integrations intact.
 */

type EventProps = Record<string, string | number | boolean | undefined>;

interface AnalyticsWindow {
  dataLayer?: unknown[];
  gtag?: (command: "event", eventName: string, params?: EventProps) => void;
  plausible?: (event: string, opts?: { props?: EventProps }) => void;
  posthog?: { capture: (event: string, props?: EventProps) => void };
}

const GA4_EVENT_MAP: Record<string, string> = {
  signup_form_submitted: "sign_up",
};

export function track(event: string, props: EventProps = {}) {
  if (typeof window === "undefined") return;
  const w = window as unknown as AnalyticsWindow;

  try {
    // GA4 — gtag.js is installed globally in index.html.
    // Use Google's recommended event name where DropyCash already has an
    // equivalent internal event (for example sign_up).
    const ga4Event = GA4_EVENT_MAP[event] ?? event;
    w.gtag?.("event", ga4Event, props);

    // Keep a structured dataLayer event for GTM compatibility if GTM is added later.
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...props });
    }

    w.plausible?.(event, { props });
    w.posthog?.capture(event, props);
    window.dispatchEvent(new CustomEvent("app:analytics", { detail: { event, props } }));

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[analytics]", event, props);
    }
  } catch {
    // Analytics must never break the app.
  }
}

/** Signup CTA anywhere on the marketing site. `source` identifies the placement. */
export const trackSignupCtaClick = (source: string, extra: EventProps = {}) =>
  track("signup_cta_click", { source, ...extra });

/** Successful account creation. Sent to GA4 as the recommended `sign_up` event. */
export const trackSignupSubmitted = (extra: EventProps = {}) =>
  track("signup_form_submitted", { method: "email", ...extra });

/** Fired when SignUpPage redirect actually reaches the signup tab. */
export const trackSignupFlowStarted = (extra: EventProps = {}) =>
  track("signup_flow_started", extra);

/** Successful authentication. */
export const trackLogin = (extra: EventProps = {}) =>
  track("login", { method: "email", ...extra });

/** Seller viewed a marketplace product. */
export const trackProductView = (product: { id: string; name: string; price?: number }) =>
  track("view_item", {
    item_id: product.id,
    item_name: product.name,
    value: product.price,
    currency: "MAD",
  });

/** Seller successfully created a COD order. This is not revenue yet. */
export const trackOrderCreated = (order: { id: string; orderNumber: string; value: number; itemCount: number; testMode?: boolean }) =>
  track("order_created", {
    order_id: order.id,
    transaction_id: order.orderNumber,
    value: order.value,
    currency: "MAD",
    item_count: order.itemCount,
    test_mode: Boolean(order.testMode),
  });

/**
 * Revenue should only be recorded when an order is actually delivered.
 * Call this from the trusted delivery-status flow/backend, not at order creation.
 */
export const trackDeliveredPurchase = (order: { transactionId: string; value: number }) =>
  track("purchase", {
    transaction_id: order.transactionId,
    value: order.value,
    currency: "MAD",
  });
